import katex from 'katex';
import { espacerMilliersHtml, espacerMilliersTex } from '../../utils/milliers';
import { membresChaine } from '../../utils/chaineEgalites';

/**
 * Helpers partagés du « texte à trous sur équation » — utilisés par le renderer
 * de preview (TexteTrouRenderer) ET par la passation (TexteTrouEleve), pour que
 * les deux restent synchrones.
 */

/**
 * Découpe les items d'une phrase à trous (mode texte) en LIGNES : un item
 * `text` contenant des `\n` est scindé, chaque `\n` ouvre une ligne ; les
 * autres items (trou, groupe insécable) rejoignent la ligne courante.
 * Chaque ligne se rend ensuite dans son propre bloc, comme le mode KaTeX
 * global : une grande fraction d'une ligne ne peut plus chevaucher la
 * suivante (gen_600, gen_923, retours écran 2026-09-06, l'interligne seul
 * ne suffisait pas).
 *
 * @param {Array<{type: string, content?: string}>} items
 * @returns {Array<Array<object>>} lignes, sans ligne vide
 */
export const enLignes = (items) => {
    const lignes = [[]];
    for (const item of items) {
        if (item.type !== 'text' || !item.content.includes('\n')) {
            lignes[lignes.length - 1].push(item);
            continue;
        }
        item.content.split('\n').forEach((morceau, i) => {
            if (i > 0) lignes.push([]);
            if (morceau) lignes[lignes.length - 1].push({ ...item, content: morceau });
        });
    }
    return lignes.filter((l) => l.length > 0);
};

/**
 * TABLEAU : quand TOUTES les lignes ont la forme « libellé : contenu »
 * (« Étape 1 : 5 », « Étape 6 : ⬚ »), elles se rendent en deux colonnes, les
 * contenus alignés les uns sous les autres. Sans cet alignement, « Étape 5 »
 * et « Étape 12 » n'ont pas la même largeur et les champs se décalent, alors
 * que ces séries SONT un tableau étape par nombre (demande prof 2026-09-10 ;
 * le programme de CM2 associe déjà le tableau à ces motifs).
 * Règle générale, pas un cas particulier : elle sert toute série écrite
 * « Rang 2 : … », « Terme 3 : … », etc.
 *
 * @returns {Array<{gauche: object[], droite: object[]}> | null}
 */
export const enTableau = (lignes) => {
    if (!Array.isArray(lignes) || lignes.length < 2) return null;
    const decoupe = lignes.map((ligne) => {
        const gauche = [];
        const droite = [];
        let coupe = false;
        for (const it of ligne) {
            if (coupe || it.type !== 'text') { (coupe ? droite : gauche).push(it); continue; }
            const i = it.content.indexOf(' : ');
            if (i === -1) { gauche.push(it); continue; }
            gauche.push({ ...it, content: `${it.content.slice(0, i)} :` });
            coupe = true;
            const apres = it.content.slice(i + 3);
            if (apres) droite.push({ ...it, content: apres });
        }
        return coupe && droite.length ? { gauche, droite } : null;
    });
    return decoupe.every(Boolean) ? decoupe : null;
};

/**
 * Une ligne (sortie d'enLignes) qui est une CHAÎNE d'égalités (« 1 m³ = 1 m ×
 * 1 m × 1 m = ⬚ dm × ⬚ dm × ⬚ dm = ⬚ dm³ », cf. utils/chaineEgalites.js) est
 * rendue en tête + membres « = … », chacun insécable : elle ne peut se
 * replier qu'au « = », les suites alignées sous le premier « = » (constat
 * prof sur 1059, 2026-09-09). Les trous et groupes insécables sont atomiques,
 * seuls les items texte se découpent.
 *
 * @returns {{ tete: object[], membres: object[][] } | null}
 */
export const enChaine = (ligne) => {
    const texteDe = (it) => {
        if (it.type === 'text') return it.content;
        if (it.type === 'group') return it.items.map(texteDe).join('');
        return '…';
    };
    if (!membresChaine(ligne.map(texteDe).join(''))) return null;
    const membres = [[]];
    for (const it of ligne) {
        if (it.type !== 'text') { membres[membres.length - 1].push(it); continue; }
        it.content.split(' = ').forEach((p, i) => {
            if (i > 0) membres.push([{ type: 'text', content: `= ${p}` }]);
            else if (p) membres[membres.length - 1].push({ type: 'text', content: p });
        });
    }
    return { tete: membres[0], membres: membres.slice(1) };
};

/** Sépare les triples accolades {{{ → { {{ et }}} → }} } pour que le regex des
 *  trous {{...}} ne mange pas les accolades KaTeX de \dfrac{...}. */
export const preprocessBraces = (s) =>
    (s || '').replace(/\{\{\{/g, '{ {{').replace(/\}\}\}/g, '}} }');

/**
 * Étai vertical invisible, à placer JUSTE AVANT le placeholder d'un trou.
 *
 * Sans lui, la BARRE d'un \dfrac disparaît sous le champ de saisie. Le champ
 * est superposé au trou et fait 24 px de haut au minimum, alors que la boîte du
 * trou n'en mesure qu'environ 14 : il déborde donc de 5 px de chaque côté, et
 * KaTeX ne laisse que 0,447 em (soit 6,3 px) entre la base du numérateur et la
 * barre. Mesuré sur la vlist émise par KaTeX (retour utilisateur 2026-08-02,
 * sur transformer_texte_trou_saisie de l'attendu 901) :
 *
 *              base num. → barre    barre → base dén.    seuil à 14 px
 *   sans étai        0,447 em            0,916 em        0,61 / 1,11 em
 *   avec étai        0,760 em            1,420 em
 *
 * L'étai est de largeur NULLE (rien ne bouge horizontalement) et se place hors
 * du \htmlClass : la boîte mesurée pour positionner le champ reste celle du
 * \phantom, seule la barre s'écarte. Profondeur 0,6 em sous la ligne de base,
 * hauteur 1,3 em au-dessus.
 */
export const ETAI_TROU = '\\rule[-0.6em]{0pt}{1.9em}';

/**
 * Un mot attendu d'un trou → LaTeX inséré DANS l'équation (mode KaTeX global).
 * Un nombre garde sa virgule collée (« 4,15 », pas « 4, 15 » : en mode maths la
 * virgule est une ponctuation espacée) ; une lettre seule ou du LaTeX passent
 * tels quels ; un mot (« multiplié », « médiatrice ») passe en \text{} pour
 * garder ses accents et sa police droite. Sert à la correction sur place
 * (TexteTrouEleve, lot 4) ; venait de BonneReponseEleve, supprimé au lot 7.
 */
export const slotLatex = (mot) => {
    const s = String(mot || '').trim();
    if (/^-?\d+(?:[,.]\d+)?$/.test(s)) return s.replace(',', '{,}');
    if (/[\\^_{}]/.test(s) || /^[A-Za-zπ]$/.test(s)) return s;
    return `\\text{${s}}`;
};

/** Détecte si la phrase contient au moins une ligne KaTeX globale ($...$). */
export const hasMathLines = (phrase) =>
    (phrase || '').split('\n').some((line) => {
        const t = line.trim();
        return t.startsWith('$') && t.endsWith('$') && t.length > 2;
    });

/**
 * Reconstruit l'équation ENTIÈRE (mode KaTeX global) en remplaçant chaque trou
 * {{...}} par un placeholder, puis rend chaque ligne via KaTeX.
 *
 * @param {string} phrase - phrase avec trous {{...}}
 * @param {string|function} slotMarkup - markup LaTeX du placeholder :
 *   - string : placeholder fixe (ex. preview : `\colorbox{...}{\phantom{00}}`)
 *   - (index) => string : placeholder indexé/repérable (ex. élève :
 *     `\htmlClass{tt-slot tt-slot-${index}}{\phantom{00}}`)
 * @returns {string|null} HTML KaTeX, ou null si la phrase n'a pas de ligne $...$.
 */
export const buildMathHtml = (phrase, slotMarkup) => {
    if (!phrase || !hasMathLines(phrase)) return null;
    const safe = preprocessBraces(phrase);

    let counter = 0;
    // Le callback reçoit (index, contenu du trou) : le contenu permet de
    // dimensionner le slot sur le mot attendu (« multiplié » débordait du
    // \phantom{00} fixe — fix 2026-08-02). Markup chaîne : inchangé.
    const replacer = typeof slotMarkup === 'function'
        ? (m, contenu) => slotMarkup(counter++, contenu)
        : () => slotMarkup;
    const fullExpr = safe.replace(/\{\{([^}]+)\}\}/g, replacer);

    const htmlParts = [];
    for (const line of fullExpr.split('\n')) {
        const t = line.trim();
        if (!t) continue;
        if (t.startsWith('$') && t.endsWith('$') && t.length > 2) {
            // Ligne entièrement KaTeX → display mode (centré)
            try {
                htmlParts.push(katex.renderToString(espacerMilliersTex(t.slice(1, -1)), {
                    throwOnError: false,
                    displayMode: true,
                    trust: true,
                }));
            } catch {
                htmlParts.push(t);
            }
        } else if (t.includes('$')) {
            // Ligne mixte texte + $...$ inline
            const rendered = t.replace(/\$([^$]+)\$/g, (_, tex) => {
                try {
                    return katex.renderToString(espacerMilliersTex(tex), { throwOnError: false, displayMode: false, trust: true });
                } catch {
                    return `$${tex}$`;
                }
            });
            htmlParts.push(`<div class="my-1">${espacerMilliersHtml(rendered)}</div>`);
        } else {
            htmlParts.push(`<div class="my-1">${espacerMilliersHtml(t)}</div>`);
        }
    }
    return htmlParts.join('');
};
